import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface RouteInvite {
  id: string;
  routeId: string;
  routeName: string;
  stopCount: number;
  inviterId: string;
  inviterName: string;
  inviterAvatar: string | null;
  inviterUsername: string;
  status: "pending" | "accepted" | "declined";
  createdAt: string;
}

export function useRouteInvites(userId: string | undefined) {
  const [invites, setInvites] = useState<RouteInvite[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from("route_invites")
      .select("id, route_id, status, created_at, inviter_id")
      .eq("invitee_id", userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (!data || data.length === 0) { setInvites([]); setLoading(false); return; }

    const routeIds = [...new Set(data.map((d: { route_id: string }) => d.route_id))];
    const inviterIds = [...new Set(data.map((d: { inviter_id: string }) => d.inviter_id))];

    const [routesRes, profilesRes] = await Promise.all([
      supabase.from("user_routes").select("id, name").in("id", routeIds),
      supabase.from("profiles").select("id, display_name, avatar_url, username").in("id", inviterIds),
    ]);

    const routeMap: Record<string, { name: string }> = {};
    (routesRes.data ?? []).forEach((r: { id: string; name: string }) => { routeMap[r.id] = r; });

    const profileMap: Record<string, { display_name: string | null; avatar_url: string | null; username: string }> = {};
    (profilesRes.data ?? []).forEach((p: { id: string; display_name: string | null; avatar_url: string | null; username: string }) => {
      profileMap[p.id] = p;
    });

    // Get stop counts
    const { data: stopData } = await supabase
      .from("route_stops")
      .select("route_id")
      .in("route_id", routeIds);
    const stopCounts: Record<string, number> = {};
    (stopData ?? []).forEach((s: { route_id: string }) => {
      stopCounts[s.route_id] = (stopCounts[s.route_id] ?? 0) + 1;
    });

    setInvites(
      data.map((d: { id: string; route_id: string; status: string; created_at: string; inviter_id: string }) => {
        const profile = profileMap[d.inviter_id];
        return {
          id: d.id,
          routeId: d.route_id,
          routeName: routeMap[d.route_id]?.name ?? "Bilinmeyen Rota",
          stopCount: stopCounts[d.route_id] ?? 0,
          inviterId: d.inviter_id,
          inviterName: profile?.display_name || profile?.username || "Kullanıcı",
          inviterAvatar: profile?.avatar_url ?? null,
          inviterUsername: profile?.username ?? "",
          status: d.status as "pending" | "accepted" | "declined",
          createdAt: d.created_at,
        };
      }),
    );
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const acceptInvite = useCallback(
    async (invite: RouteInvite, currentUserId: string) => {
      // 1. Mark as accepted
      await supabase.from("route_invites").update({ status: "accepted" }).eq("id", invite.id);

      // 2. Fetch stops from original route
      const { data: stops } = await supabase
        .from("route_stops")
        .select("*")
        .eq("route_id", invite.routeId)
        .order("order_index", { ascending: true });

      if (!stops) return null;

      // 3. Create a copy of the route for the invitee, linked to inviter
      const { data: newRoute } = await supabase
        .from("user_routes")
        .insert({
          user_id: currentUserId,
          name: invite.routeName,
          is_tracking: false,
          partner_user_id: invite.inviterId,
        })
        .select()
        .maybeSingle();

      if (!newRoute) return null;

      // 4. Insert stops
      if (stops.length > 0) {
        await supabase.from("route_stops").insert(
          stops.map((s: Record<string, unknown>, idx: number) => ({
            route_id: newRoute.id,
            venue_id: s.venue_id,
            venue_name: s.venue_name,
            category: s.category,
            category_slug: s.category_slug,
            district: s.district,
            address: s.address,
            image: s.image,
            lat: s.lat,
            lng: s.lng,
            completed: false,
            order_index: idx,
          })),
        );
      }

      // 5. Update inviter's original route to link back to invitee
      await supabase
        .from("user_routes")
        .update({ partner_user_id: currentUserId, partner_route_id: newRoute.id })
        .eq("id", invite.routeId);

      await fetchInvites();
      return newRoute.id as string;
    },
    [fetchInvites],
  );

  const declineInvite = useCallback(
    async (inviteId: string) => {
      await supabase.from("route_invites").update({ status: "declined" }).eq("id", inviteId);
      await fetchInvites();
    },
    [fetchInvites],
  );

  return { invites, loading, acceptInvite, declineInvite, refetch: fetchInvites };
}
